import React from "react";
import { useLocaleNavigate } from "../lib/i18n";
import { ShieldCheck, Store, Crown } from "lucide-react";

/**
 * Compact dealer badge for listing cards / detail pages.
 *
 * `dealer` shape:
 *   { is_dealer: bool, verified: bool, verification_level?: "standard"|"premium",
 *     company_name: str|null, slug: str|null, logo_url: str|null }
 *
 * If `verified && slug`, the badge becomes a link to /dealers/{slug}.
 * Phase 65 — premium-verified dealers get a gold accent.
 */
export function DealerBadge({ dealer, size = "sm" }) {
  const nav = useLocaleNavigate();
  if (!dealer || !dealer.is_dealer) return null;
  const verified = !!dealer.verified;
  const isPremium = verified && dealer.verification_level === "premium";
  const linkable = verified && !!dealer.slug;
  const padding = size === "lg" ? "px-3 py-1.5 text-xs" : "px-2 py-0.5 text-[10px]";
  const tone = isPremium
    ? "border-amber-400/40 bg-gradient-to-r from-amber-500/15 to-amber-400/10 text-amber-200"
    : verified
      ? "border-red-500/30 bg-red-600/10 text-red-300"
      : "border-zinc-700 bg-zinc-900 text-zinc-300";
  const Icon = isPremium ? Crown : verified ? ShieldCheck : Store;
  const inner = (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-bold uppercase tracking-wider ${padding} ${tone}`}
      data-testid="dealer-badge"
      data-verified={verified ? "true" : "false"}
      data-level={dealer.verification_level || "none"}
    >
      <Icon className="h-3 w-3" />
      <span className="truncate max-w-[140px]">{dealer.company_name || "Händler"}</span>
      {isPremium ? (
        <span className="text-[9px] opacity-80">premium</span>
      ) : verified ? (
        <span className="text-[9px] opacity-80">verifiziert</span>
      ) : null}
    </span>
  );
  if (linkable) {
    // Phase 66 — use a role="link" span instead of <a> because the badge is
    // commonly rendered inside an outer <Link> (CarCard). Nested <a> elements
    // are invalid HTML and break React 18 hydration. We still navigate via
    // useNavigate so SPA-routing semantics are preserved.
    const go = (e) => {
      e.preventDefault();
      e.stopPropagation();
      nav(`/dealers/${dealer.slug}`);
    };
    return (
      <span
        role="link"
        tabIndex={0}
        onClick={go}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") go(e); }}
        className="inline-block cursor-pointer hover:opacity-80 transition-opacity outline-none focus:ring-2 focus:ring-red-500/40 rounded-full"
        data-testid="dealer-badge-link"
      >
        {inner}
      </span>
    );
  }
  return inner;
}
